<template>
  <Modal ref="modalRef" @ok="createFamilyGroup">
    <template #title>Share my tree</template>
    <div class="share-diagram">
      <div class="share-rg">
        <!-- <div class="step-num">1</div> -->
        <img :src="researchGroupIconUrlRef" class="rg-icon"/>
        <img src="@/assets/icons/arrow_right_150.png" class="arrow"/>
      </div>
      <div class="share-living">
        <!-- <div class="step-num">2</div> -->
        <img src="@/assets/icons/group_gray_150.png" class="living-icon"/>
        <img src="@/assets/icons/arrow_right_150.png" class="arrow"/>
      </div>
      <div class="group">
        Group
      </div>
      <div class="invite-members">
        <!-- <div class="step-num">3</div> -->
        <img src="@/assets/icons/group_gray_150.png" class="member-icon"/>
        <img src="@/assets/icons/arrow_right_150.png" class="arrow"/>
      </div>
    </div>
    <p>To share your tree, create a <b>group</b> and:</p>
    <div class="instructions">
      <ul class="list-unstyled">
        <li><span class="step-num">1</span> <b>Share</b> your tree with the group</li>
        <li><span class="step-num">2</span> <b>Choose</b> which living people should be visible</li>
        <li><span class="step-num">3</span> <b>Invite</b> people to join the group</li>
      </ul>
    </div>
    <template #okText>Create a group</template>
  </Modal>
</template>

<style lang="scss" scoped>
.modal-dialog {
  min-width: 600px;
}

:deep(.modal-body) {
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

p {

  @media (min-width: 768px) {
    margin-left: 1rem;
  }
}

.step-num {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bs-primary-bg-subtle);
  border-radius: 50%;
}

.share-diagram {
  align-self: center;
  margin-bottom: 1rem;
  display: grid;
  grid-template-columns: repeat(3, max-content);
  column-gap: 1rem;
  justify-content: center;

  .share-rg, .share-living, .invite-members {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  --icon-width: 2rem;

  @media (min-width: 768px) {
    --icon-width: 3rem;
  }

  .rg-icon {
    width: var(--icon-width);
  }

  .share-living { 
    grid-row: 2;
  }

  .arrow {
    width: 1.5rem;

    @media (min-width: 768px) {
      width: 2rem;
    }
  }

  .living-icon {
    width: var(--icon-width);
    opacity: 30%;
  }

  .member-icon {
    width: var(--icon-width);
    opacity: 70%;
  }

  .group {
    grid-row: 1 / span 2;
    width: 80px;
    height: 80px;
    border: 4px dotted var(--bs-primary-bg-subtle);
    border-radius: 16px;
    padding: 0.5rem 0;
    font-size: 0.875rem;
    text-align: center;

    @media (min-width: 768px) {
      width: 130px;
      height: 130px;
      font-size: 1rem;
    }
  }

  .invite-members {
    grid-row: 1 / span 2;
    flex-direction: row-reverse;

    .arrow {
      transform: rotate(180deg);
    }
  }
}

.instructions {
  padding: 0 1.5rem;

  ul {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

}
</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useWorkspaceStore } from '@/rd/WorkspaceStore'
import { workspaceTypeInfos } from '@/rd/ResearchDataModel'
import { useUserStore } from '@/gp/UserStore'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { FamilyGroup } from '@/gp/GroupAdminModel'
import { LoadMode } from '@/util/AsyncData'
import { isDefined } from '@/util/TypeScriptUtil'
import { minBy } from 'lodash'
import { getPossessive } from '@/util/LanguageUtil'
import Modal from '@/util/Modal.vue'
import { AsyncUtil } from '@/util/AsyncUtil'

defineExpose({
  open: () => modalRef.value?.open(),
})

const router = useRouter()
const dataGroupStore = useDataGroupStore()
const workspaceStore = useWorkspaceStore()
const userStore = useUserStore()
const familyGroupStore = useFamilyGroupStore()

const researchGroupIconUrlRef = computed(() => {
  const rgs = dataGroupStore.getOwnedResearchGroups(LoadMode.EnsureLoaded)
  if (!rgs.length) // should never happen
    return ''

  const ws = workspaceStore.getAllAsyncWorkspaces(LoadMode.EnsureLoaded)
    .map(a => a.data).filter(isDefined).filter(w => rgs.some(rg => rg.id == w.viewGroupId))
  
  if (!ws.length)
    return ''

  const mostRecentWksp = minBy(ws, w => w.modifiedDate!.valueOf())
  return workspaceTypeInfos.find(t => t.id == mostRecentWksp?.dataFileBaseType)?.icon
})

const modalRef = ref<InstanceType<typeof Modal>>()

async function createFamilyGroup() {  
  try {
    const selfUser = userStore.getAsyncSelf()?.data // should be loaded

    if (selfUser) {
      const fg = new FamilyGroup()
      fg.name = FamilyGroup.getDefaultName(selfUser)
      
      const fgId = await AsyncUtil.atLeast(500, familyGroupStore.addAsync(fg))

      await modalRef.value?.close()
      router.push({ name: 'familyGroup.researchGroupsTab', params: { familyGroupId: fgId} })
    }
  }
  finally {
    // TODO: notify modal not busy
  }
}
</script>
